// src/components/SEO.js
import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

const SEO = ({
  title,
  description,
  image,
  canonicalUrl,
  faqSchema, 
}) => {
  const location = useLocation();

  // 1. Force lowercase and strip trailing slashes uniformly
  const cleanPath = location.pathname.toLowerCase().replace(/\/$/, "");
  
  // FIXED: Adjusted apex domain fallback to synchronize with manual routing values
  const canonical =
    canonicalUrl ||
    `https://investorlanes.com${cleanPath || "/"}`;

  // 2. Production Fallback Assets for Absolute Indexation Validity
  const defaultTitle = "InvestorLanes | Connect with Global Investors & Funding Guides";
  const defaultDesc = "Discover verified startup investors, dynamic funding guides, and country-specific venture capital data on InvestorLanes.";
  const defaultImage = "https://investorlanes.com"; // FIXED: Added direct graphic asset file path

  const seoTitle = title || defaultTitle;
  const seoDesc = description || defaultDesc;
  const seoImage = image || defaultImage;

  // 3. Dynamic FAQ Schema Markup Generator
  const jsonLdFaq = faqSchema && faqSchema.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSchema.map(item => ({
      "@type": "Question",
      "name": item.question || item.q, // Defensive optimization to accept both data forms safely
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer || item.a
      }
    }))
  } : null;

  return (
    <Helmet>
      {/* Primary HTML Meta Headers */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDesc} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content="index, follow, max-image-preview:large" />

      {/* Open Graph Architecture (Facebook, LinkedIn) */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content="InvestorLanes" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Cards Engine */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDesc} />
      <meta name="twitter:image" content={seoImage} />

      {/* FAQ Schema Markup Engine Injection */}
      {jsonLdFaq && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLdFaq)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
